import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { noop, compose, extend } from 'underscore';
import { SPAv2Tutorial } from '@bingads-webui-campaign-react/spa-tutorial';
import { StyledGridViewWrapper } from '@bingads-webui-campaign-react/grid-view-wrapper';
import { TopGridPanel } from '@bingads-webui-campaign-react/top-grid-panel';
import { withGlobalFilter } from '@bingads-webui-campaign-react/global-filter-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { StyledTablePageContainer } from '@bingads-webui-campaign-react/table-page-container';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withTablePageStateManager } from '@bingads-webui-campaign-react/table-page-state-manager';
import { withCurrentExpandedScope } from '@bingads-webui-campaign-react/use-current-scope-entity';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';
import { ProgressiveLoadingBlock } from '@bingads-webui-campaign-react/shell-ui-common';

import { createBrandsGridForUiNext } from './brands-factory';
import { getToolbarConfig } from './toolbar-config';

import { StyledAddBrandPanel } from './add-brand-panel';

const StyledGridViewWrapperWithFilterContext = withGlobalFilter(StyledGridViewWrapper);

const getBrandGridViewStyles = () =>
  ({
    '@global': extend({
      '.brand-page .table-container table.table thead.header tr th': {
        '&:first-child, &:nth-child(2)': {
          'border-right': '1px solid #EDEBE9',
        },
        '&:nth-child(5)': {
          'border-right': 'none',
        },
        '&:last-child': {
          'border-left': 'none',
        },
      },
      '.brand-page .table-container table.table tbody tr td': {
        'overflow-wrap': 'break-word',
        '&:first-child, &:nth-child(2)': {
          'border-right': '1px solid #EDEBE9',
        },
        '&:nth-child(5)': {
          'border-right': 'none',
          '&:hover': {
            'border-right': 'none',
          },
        },
        '&:last-child': {
          'border-left': 'none',
          '&:hover': {
            'border-left': 'none',
          },
        },
      },
      '.brand-page .bingadsgrid .table-container table.table tbody tr:hover': {
        '& td': {
          borderRight: '1px solid #fff',
        },
      },
      '.brand-page .bingadsgrid': {
        paddingRight: 32,
      },
      '.brand-page .container-fluid': {
        marginRight: 32,
        height: 56,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      },
      '.brand-page .top-grid-form': {
        maxWidth: 'none',
      },
      '.grid-header-text': {
        whiteSpace: 'nowrap',
      },
      '.show-brand-dropdown': {
        border: 'none',
        background: 'none',
        width: 17,
        '&:hover, &:focus, &:active, &:visited': {
          'background-color': 'white',
          border: '1px solid #EDEBE9',
        },
      },
      '.brand-dropdown': {
        position: 'absolute',
        'font-weight': 600,
        height: 32,
        'line-height': 1.5,
        'min-width': 80,
        outline: 'transparent',
        padding: '0px 20px',
        'text-decoration': 'none',
        border: '1px solid #EDEBE9',
        'background-color': 'white',
        '&:active, &:focus': {
          'background-color': 'white',
          border: '1px solid #EDEBE9',
        },
        '&:hover': {
          'background-color': '#FDFBF9',
        },
      },
      '.delete-brand': {
        background: 'none',
        'vertical-align': 'middle',
        '&:active, &:focus, &:hover': {
          border: 'none',
        },
      },
    }),
  });

class Brands extends React.PureComponent {
  constructor(props) {
    super(props);
    const {
      serverContext,
      scope,
      preferenceService,
      permissions,
      crossPageSelectPlugin,
      toolbarPlugin,
      onGridInjected,
      showTopOfGridPanel,
      currentCampaign,
      currentAdGroup,
      siteMap,
      perfMarker,
      classes,
    } = props;

    this.classes = classes;

    this.state = {
      backboneGridView: null,
      addBrandPanel: {
        isOpenAddBrandPanel: false,
        displayName: '',
      },
    };
    this.columnAction = noop;
    this.chartView = null;
    this.viewport = React.createRef();

    const brandsGridPromise = createBrandsGridForUiNext({
      crossPageSelectPlugin,
      serverContext,
      scope,
      preferenceService,
      permissions,
      toolbarPlugin,
      currentCampaign,
      currentAdGroup,
      siteMap,
      perfMarker,
    });

    const BrandsGridView = React.lazy(() => { // eslint-disable-line arrow-body-style
      return brandsGridPromise.then(({ gridView, i18n }) => {
        this.GRID_EVENT_NAMES = gridView.EVENT_NAMES;
        this.brandGrid = gridView;
        this.gridView = gridView.grid.gridView;
        this.bindGridEvents();
        gridView.overrideWith = showTopOfGridPanel; // eslint-disable-line no-param-reassign
        onGridInjected({
          backboneGridView: gridView,
          deselectAll: gridView.grid.gridView.deselectAll.bind(gridView.grid.gridView),
        });
        this.i18n = i18n;

        this.setState({ backboneGridView: gridView });

        return {
          default: () => (<StyledGridViewWrapperWithFilterContext gridView={gridView} />),
        };
      });
    });

    this.productFeedGrid = (<BrandsGridView />);
  }

  componentWillUnmount() {
    if (this.gridView) {
      this.gridView.off(this.GRID_EVENT_NAMES.SHOW_ADD_BRAND_PANEL, this.showAddBrandPanel);
    }
  }

  bindGridEvents = () => {
    if (this.gridView) {
      this.gridView.on(this.GRID_EVENT_NAMES.SHOW_ADD_BRAND_PANEL, this.showAddBrandPanel);
    }
  };

  showAddBrandPanel = ({
    displayName,
  }) => {
    this.setState({
      addBrandPanel: {
        isOpenAddBrandPanel: true,
        displayName,
      },
    });
  };

  closeAddBrandPanel = () => {
    this.setState({
      addBrandPanel: {
        isOpenAddBrandPanel: false,
      },
    });
  };

  render() {
    const {
      panelView,
      renderToolbarContainer,
      showTopGridPanel,
      i18n,
    } = this.props;

    const topGridPanel = showTopGridPanel ? (
      <TopGridPanel
        panelView={panelView}
        customClasses={{
          topGridPanelCustomStyles: this.props.classes.topGridPanelCustomStyles,
        }}
      />) : null;

    const topOfGridContent = (
      <Fragment>
        <SPAv2Tutorial i18n={i18n} />
        {topGridPanel}
      </Fragment>
    );

    const stickyTopOfGridContent = (
      <Fragment>
        {renderToolbarContainer()}
      </Fragment>
    );

    return (
      <React.Suspense fallback={<ProgressiveLoadingBlock />}>
        <StyledTablePageContainer
          topOfGridContent={topOfGridContent}
          stickyTopOfGridContent={stickyTopOfGridContent}
          gridView={this.productFeedGrid}
          backboneGridView={this.state.backboneGridView}
          customClasses={{
            tablePageContainer: 'brand-page',
            gridViewContainer: 'brand-table',
          }}
        />
        {this.state.addBrandPanel ? <StyledAddBrandPanel
          classes={this.classes}
          displayName={this.state.addBrandPanel.displayName}
          isOpen={this.state.addBrandPanel.isOpenAddBrandPanel}
          onClose={this.closeAddBrandPanel}
          brandGrid={this.brandGrid}
          i18n={i18n}
        /> : null}
      </React.Suspense>
    );
  }
}

Brands.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
  }).isRequired,
  serverContext: PropTypes.shape({
    CurrentCustomer: PropTypes.object.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    gridContainer: PropTypes.string,
    inlineChartContainer: PropTypes.string.isRequired,
    pageContainer: PropTypes.string.isRequired,
    topGridPanelCustomStyles: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  }).isRequired,
  scope: PropTypes.shape({}).isRequired,
  preferenceService: PropTypes.shape({}).isRequired,
  permissions: PropTypes.shape({}).isRequired,
  currentCampaign: PropTypes.shape({}).isRequired,
  currentAdGroup: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  renderToolbarContainer: PropTypes.func.isRequired,
  crossPageSelectPlugin: PropTypes.shape({
    plugin: PropTypes.func.isRequired,
  }).isRequired,
  toolbarPlugin: PropTypes.shape({
    plugin: PropTypes.func.isRequired,
  }).isRequired,
  panelView: PropTypes.shape({
    render: PropTypes.func,
  }).isRequired,
  onGridInjected: PropTypes.func.isRequired,
  showTopGridPanel: PropTypes.bool.isRequired,
  showTopOfGridPanel: PropTypes.func.isRequired,
  siteMap: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
  })).isRequired,
  perfMarker: perfMarkerPropTypes.isRequired,
};

Brands.defaultProps = {
};

export const StyledBrand = compose(
  withI18nConsumer,
  WrappedComponent => withDefaultStyles(WrappedComponent, getBrandGridViewStyles),
  withCurrentExpandedScope,
  withTablePageStateManager({ getToolbarConfig })
)(Brands);
